import {
  crouch,
  DEMO_STEP,
  E,
  flick,
  graffitiLine,
  hold,
  N,
  ride,
  roll,
  stepOff,
  tap,
  targetsLine,
  up,
  W,
  type DemoLine,
  type TagWall,
  type TargetRow,
} from "./script";

// park's demo lines, played in this order (`DEMO_LINES` in `demo.ts`).

/** The spot the targets line shoots from, and the row (the map file's `targets`). */
const TARGETS: TargetRow = {
  range: { x: -25, z: -9 },
  row: [
    [-33.5, -17.5],
    [-32.8, -13.5],
    [-32.7, -11.1],
    [-33, -9],
    [-32.7, -6.9],
  ],
};

/** The graffiti on the north fence (the map file's `graffiti`), and where to stand to wash each tag. */
const GRAFFITI: TagWall = {
  face: N,
  stands: [
    [22, -32.8],
    [26.5, -32.8],
    [31, -32.8],
  ],
};

// Built on call so the hill site drops them (#229; see `parkV2Lines`).
export const parkLines = (): DemoLine[] => [
  {
    id: "park-funbox",
    name: "Funbox flip, manual, vert revert into a manual",
    mapId: "park",
    start: { x: 0, z: 16, yaw: N, speed: 6 },
    segments: [
      crouch(1.1),
      roll(0.02),
      tap("flip"),
      roll(0.55),
      ...flick([0, 1], [0, -1]),
      ride(1.3),
      crouch(0.15),
      roll(0.02),
      tap("flip", 1),
      roll(1.45),
      hold("grab", 0.6),
      roll(0.35),
      tap("revert"),
      // Air, revert, manual: one combo (#69).
      roll(0.1),
      ...flick([0, 1], [0, -1]),
      ride(0.8),
      crouch(0.15),
      roll(1),
    ],
    lands: [
      "Kickflip",
      "Funbox Transfer",
      "Manual",
      "Funbox Manual",
      "Heelflip",
      "Method",
      "Revert",
    ],
  },
  {
    id: "park-rail",
    name: "Grind switches, then a special on vert",
    mapId: "park",
    start: { x: 12.5, z: 2, yaw: N, speed: 9 },
    special: true,
    segments: [
      crouch(0.5),
      roll(0.12),
      tap("grind"),
      ride(0.5),
      tap("grind", 0, 1),
      ride(0.4),
      tap("grind", -0.7, 0.7),
      ride(1),
      roll(0.6),
      ...flick([0, 1], [0, -1]),
      hold("grab", 0.6),
      roll(0.9),
      roll(1.5, 0.6),
    ],
    lands: ["50-50", "Rail Hop", "Nosegrind", "Crooked", "Christ Air"],
  },
  {
    id: "park-specials",
    name: "Darkslide, McTwist, Kickflip Underflip",
    mapId: "park",
    start: { x: 12.5, z: 2, yaw: N, speed: 9 },
    special: true,
    // Left, down before the grind and flip specials (#135), right, down before the grab one.
    segments: [
      crouch(0.5),
      roll(0.02),
      ...flick([-1, 0], [0, -1]),
      tap("grind"),
      ride(1.4),
      roll(1.2),
      // Right, down + grab just after the vert launch; it turns by itself.
      ...flick([1, 0], [0, -1]),
      tap("grab"),
      roll(1.8),
      crouch(0.3),
      roll(0.02),
      ...flick([-1, 0], [0, -1]),
      tap("flip"),
      roll(0.9),
      roll(1.5, -0.6),
    ],
    lands: ["Darkslide", "Rail Hop", "McTwist", "Kickflip Underflip"],
  },
  {
    id: "park-wallplant",
    name: "Nollie flip into a wallplant, FS 180 shove-it",
    mapId: "park",
    start: { x: -24, z: 0, yaw: W, speed: 9 },
    segments: [
      roll(0.1),
      tap("nollie"),
      crouch(0.3),
      roll(0.02),
      tap("flip"),
      roll(0.48),
      tap("ollie"),
      roll(1.05),
      crouch(0.3),
      roll(0.02),
      tap("flip", 0, -1),
      roll(0.45, -1),
      roll(0.45),
      roll(1, -0.6),
    ],
    // The shove-it spins left (counterclockwise, frontside) and comes down straight: Perfect.
    lands: ["Nollie Kickflip", "Wallplant", "FS 180 Pop Shove-It"],
  },
  {
    id: "park-wallride",
    name: "Wallride the fence, wallie out, 360 Flip",
    mapId: "park",
    // Rolling north beside the west fence, angled into it. Grind held into
    // the fence rides it; ollie off it is a Wallie, with a 360 Flip (up-right) after.
    start: { x: -32, z: 14, yaw: N + 0.44, speed: 9 },
    segments: [
      crouch(0.3),
      roll(0.02),
      hold("grind", 1.2),
      roll(0.02),
      tap("ollie"),
      roll(0.25),
      tap("flip", 0.7, 0.7),
      roll(0.7),
      roll(0.8, 0.6),
      roll(0.5),
    ],
    lands: ["Wallride", "Wallie", "360 Flip"],
  },
  {
    id: "park-acid",
    name: "Acid drop into the halfpipe",
    mapId: "park",
    start: { x: -28, z: -4, yaw: N, speed: 9 },
    segments: [
      roll(1.78),
      crouch(0.1),
      roll(0.1),
      tap("revert"),
      roll(2.2),
      tap("grab", -1),
      hold("grab", 0.6),
      roll(1.8),
      tap("flip"),
      roll(0.95),
      tap("revert"),
      roll(1.5),
    ],
    lands: ["Acid Drop", "Halfpipe Drop In", "Indy", "Kickflip", "Revert"],
  },
  {
    id: "park-lip",
    name: "Invert on the halfpipe's far lip, revert out",
    mapId: "park",
    start: { x: -28, z: -26, yaw: N, speed: 9 },
    // Grind held with up all the way to the coping: an Invert. Letting go with
    // R2 drops back in (the Halfpipe Lip gap) and reverts in the same breath.
    segments: [hold("grind", 2, 0, 1), tap("revert"), roll(2)],
    lands: ["Invert", "Halfpipe Lip", "Revert"],
  },
  {
    id: "park-channel",
    name: "Kickflip into a Nosegrab across the channel",
    mapId: "park",
    // Crouch up the channel's south quarter and let go on the face (the
    // ollie waits for the lip), up held through the launch: a transfer air
    // out over the deck and down the far side (#77). Then carve west, off the fence.
    start: { x: 22, z: -2, yaw: N, speed: 9.5 },
    // Grab pressed mid-flip and held: it starts as the flip finishes (#84).
    segments: [
      up(1, true),
      up(0.35),
      roll(0.2),
      tap("flip"),
      roll(0.1),
      hold("grab", 0.6, 0, 1),
      roll(0.5),
      roll(0.5, -1),
      roll(1),
    ],
    lands: ["Kickflip", "Nosegrab", "Transfer", "Channel Transfer"],
  },
  {
    id: "park-side-transfer",
    name: "Nosegrab side transfer from the halfpipe to the big quarter",
    mapId: "park",
    // Carve up the halfpipe's north wall toward its east end: the air carries
    // along the coping over the gap and comes down the big quarter beside
    // it (#116).
    start: { x: -27, z: -26, yaw: N - 0.6, speed: 9 },
    segments: [roll(1.1), tap("grab", 0, 1), hold("grab", 0.5), roll(1.5)],
    lands: ["Nosegrab", "Transfer"],
  },
  {
    id: "park-seawall",
    name: "50-50 the seawall, kickflip out onto the beach",
    mapId: "park",
    // Angled at the seawall's park-side ledge from the north-west: ollie
    // onto it, grind east toward the beach and kickflip off into the sand,
    // which slows you to a stop (#82).
    start: { x: 20, z: 22, yaw: 0.6, speed: 9 },
    segments: [
      crouch(0.5),
      roll(0.02),
      tap("grind"),
      ride(0.35),
      tap("ollie"),
      roll(0.08),
      tap("flip"),
      roll(1.5),
    ],
    lands: ["50-50", "Seawall Grind", "Kickflip"],
  },
  {
    id: "park-seawall-hop",
    name: "50-50 the seawall, L1 hops off the side with a Heelflip",
    mapId: "park",
    // The seawall line again, but off the side (#232): L1 on the rail hops
    // you off to the left, still heading for the beach, Heelflip on the way down.
    start: { x: 20, z: 22, yaw: 0.6, speed: 9 },
    segments: [
      crouch(0.5),
      roll(0.02),
      tap("grind"),
      ride(0.35),
      tap("spinL"),
      roll(0.06),
      tap("flip", 1),
      roll(1.5),
    ],
    lands: ["50-50", "Seawall Grind", "Heelflip"],
  },
  {
    id: "park-skitch",
    name: "Skitch the beach buggy round the turn, kickflip off it",
    mapId: "park",
    // Rolling up behind the buggy as a line starts (a new skater's clock is
    // 0, so it's always in the same spot): up grabs on (#37). Towed round
    // its south turn, crouched, then let go of ollie with the stick left to
    // pop off it inside the loop, and kickflip.
    start: { x: 16, z: -8.8, yaw: 0, speed: 8 },
    // Then off the board (#19): L2 and R2 together step off, a run and a hop,
    // and the two triggers again in the hop drop the board under you: a Caveman.
    segments: [
      up(0.1),
      ride(3.1),
      { dur: 0.25, hold: ["ollie"] },
      roll(0.02, -1),
      tap("flip"),
      roll(1.5),
      ...stepOff(),
      { dur: 1.2, x: 0, y: 1 },
      { dur: 0.05, x: 0, y: 1, hold: ["ollie"] },
      { dur: 0.15, x: 0.3, y: 1 },
      ...stepOff(),
      roll(1.5),
    ],
    lands: ["Skitch", "Kickflip", "Caveman"],
  },
  {
    id: "park-run-out",
    name: "Kickflip, manual, step off and run it out into a Caveman",
    mapId: "park",
    // A run out (#19): step off out of a manual and the combo waits on foot
    // (the HUD counts it down); a run, a hop, and both triggers in the hop
    // drop the board back under you, a Caveman that lands the whole combo.
    start: { x: -4, z: 14, yaw: E, speed: 6 },
    segments: [
      crouch(0.3),
      roll(0.02),
      tap("flip"),
      roll(0.55),
      ...flick([0, 1], [0, -1]),
      ride(0.6),
      ...stepOff(),
      { dur: 1, x: 0, y: 1 },
      { dur: 0.05, x: 0, y: 1, hold: ["ollie"] },
      { dur: 0.15, x: 0, y: 1 },
      ...stepOff(),
      roll(0.6),
      roll(1.2, -0.6),
    ],
    lands: ["Kickflip", "Manual", "Caveman"],
  },
  {
    id: "park-paintball",
    name: "Step off, get the paintball gun out and paint the park",
    mapId: "park",
    // The backpack (#136): off the board, R1 brings the paintball gun out
    // of the pack, □ fires (turning between shots to spread the paint),
    // L1 puts it back for the board, and both triggers get back on.
    start: { x: -4, z: 14, yaw: E, speed: 3 },
    segments: [
      roll(0.4),
      ...stepOff(),
      { dur: 0.4, x: 0, y: 0 },
      tap("spinR"),
      { dur: 0.5, x: 0, y: 0.4 },
      tap("flip"),
      { dur: 0.35, x: 0.4, y: 0 },
      tap("flip"),
      { dur: 0.35, x: 0.4, y: 0 },
      tap("flip"),
      { dur: 0.6, x: 0, y: 0 },
      tap("spinL"),
      { dur: 0.3, x: 0, y: 0 },
      ...stepOff(),
      roll(1),
    ],
    lands: [],
    shots: 3,
  },
  // Round to face back into the park (east) before getting back on.
  targetsLine(
    "park",
    TARGETS,
    "Paint the five targets by the west wall",
    E,
    false,
  ),
  {
    id: "park-washer",
    name: "Paint the west wall, then power wash it clean",
    mapId: "park",
    // The power washer (#151): off the board by the west wall, R1 for the
    // gun and three shots spread across the wall, R1 twice more round the
    // pack past the scooter to the washer, then □ held walking up to the
    // wall (the jet climbs it) and swept across the paint until it's gone.
    start: { x: -30.5, z: 15, yaw: W, speed: 0 },
    segments: [
      roll(0.2),
      ...stepOff(),
      { dur: 0.3, x: 0, y: 0 },
      tap("spinR"),
      { dur: 0.4, x: 0, y: 0 },
      tap("flip"),
      { dur: 0.08, x: 0.5, y: 0 },
      { dur: 0.25, x: 0, y: 0 },
      tap("flip"),
      { dur: 0.16, x: -0.5, y: 0 },
      { dur: 0.25, x: 0, y: 0 },
      tap("flip"),
      { dur: 0.08, x: 0.5, y: 0 },
      { dur: 0.3, x: 0, y: 0 },
      tap("spinR"),
      { dur: 0.3, x: 0, y: 0 },
      tap("spinR"),
      { dur: 0.3, x: 0, y: 0 },
      hold("flip", 1, 0, 0.6),
      hold("flip", 0.8, 0.3, 0),
      hold("flip", 1.6, -0.3, 0),
      hold("flip", 0.8, 0.3, 0),
      { dur: 0.3, x: 0, y: 0 },
    ],
    lands: [],
    shots: 3,
    washed: 3,
  },
  graffitiLine("park", GRAFFITI, "Power wash the graffiti off the north fence"),
  {
    id: "park-bmx",
    name: "On the BMX: tailwhip the funbox, One-Foot No-Hander manual, barspin, Tabletop on vert",
    mapId: "park",
    // Rides (#141): off the board, L1 round the pack to the BMX, and both
    // triggers get on it. Then the funbox line on the bike: its bigger hop
    // carries the Tailwhip over the funbox. Grab + right in the manual holds
    // a One-Foot No-Hander (#138), and up-right on vert grabs a Tabletop (#138).
    start: { x: 0, z: 16, yaw: N, speed: 0 },
    segments: [
      roll(0.2),
      ...stepOff(),
      roll(0.3),
      tap("spinL"),
      roll(0.3),
      ...stepOff(),
      crouch(1.1),
      roll(0.02),
      tap("flip"),
      roll(0.55),
      ...flick([0, 1], [0, -1]),
      ride(0.4),
      tap("grab", 1),
      hold("grab", 0.45),
      ride(0.35),
      crouch(0.15),
      roll(0.02),
      tap("flip", 1),
      roll(1.45),
      tap("grab", 1, 1),
      hold("grab", 0.6 - DEMO_STEP),
      roll(1.5),
    ],
    lands: [
      "Tailwhip",
      "Funbox Transfer",
      "Manual",
      "One-Foot No-Hander",
      "Funbox Manual",
      "Barspin",
      "Tabletop",
    ],
    ride: "bmx",
  },
  {
    id: "park-bmx-rail",
    name: "On the BMX: Double Peg, Feeble, Smith down the rail, Tailwhip One-Footer on vert",
    mapId: "park",
    // Peg grinds by direction (#138): it locks on a Double Peg, then
    // down-right switches to a Feeble and down-left to a Smith mid-rail.
    start: { x: 12.5, z: 10.5, yaw: N, speed: 0 },
    segments: [
      roll(0.2),
      ...stepOff(),
      roll(0.3),
      tap("spinL"),
      roll(0.3),
      ...stepOff(),
      crouch(0.9),
      roll(0.6),
      tap("grind"),
      ride(0.3),
      tap("grind", 1, -1),
      ride(0.35),
      tap("grind", -1, -1),
      ride(0.35),
      roll(2.15),
      tap("flip", -1),
      roll(0.55),
      tap("grab", -1),
      hold("grab", 0.35),
      roll(0.9),
      roll(1.5, 0.6),
    ],
    lands: ["Double Peg", "Feeble", "Smith", "Tailwhip", "One-Footer"],
    ride: "bmx",
  },
  {
    id: "park-scooter",
    name: "On the scooter: 50-50, Smith, Feeble down the rail, Bri Flip and a Superman on vert",
    mapId: "park",
    // The scooter (#139): off the board, R1 twice round the pack past the
    // gun, and both triggers get on it. Deck grinds by direction down the
    // rail, then a Bri Flip (up + □) and a Superman (up + ○) on the quarter.
    start: { x: 12.5, z: 10.5, yaw: N, speed: 0 },
    segments: [
      roll(0.2),
      ...stepOff(),
      roll(0.3),
      tap("spinR"),
      roll(0.3),
      tap("spinR"),
      roll(0.4),
      ...stepOff(),
      crouch(0.9),
      roll(0.6),
      tap("grind"),
      ride(0.3),
      tap("grind", -1, -1),
      ride(0.35),
      tap("grind", 1, -1),
      ride(0.35),
      roll(2.15),
      tap("flip", 0, 1),
      roll(0.6),
      tap("grab", 0, 1),
      hold("grab", 0.3, 0, 1),
      roll(0.9),
      roll(1.5, 0.6),
    ],
    lands: ["50-50", "Smith", "Feeble", "Bri Flip", "Superman"],
    ride: "scooter",
  },
  {
    id: "park-skates",
    name: "On skates: Soul, Royale, Unity down the rail, a Flatspin and a Mute on vert",
    mapId: "park",
    // Roller skates (#140): off the board, L1 twice round the pack past the
    // bike, and both triggers put them on. Soul plate tricks by direction down
    // the rail, then a Flatspin (□) and a Mute (○) on the quarter.
    start: { x: 12.5, z: 10.5, yaw: N, speed: 0 },
    segments: [
      roll(0.2),
      ...stepOff(),
      roll(0.3),
      tap("spinL"),
      roll(0.3),
      tap("spinL"),
      roll(0.4),
      ...stepOff(),
      crouch(0.9),
      roll(0.6),
      tap("grind"),
      ride(0.3),
      tap("grind", -1, 1),
      ride(0.35),
      tap("grind", 1, 1),
      ride(0.35),
      roll(2.15),
      tap("flip"),
      roll(0.75),
      tap("grab"),
      hold("grab", 0.3),
      roll(0.9),
      roll(1.5, 0.6),
    ],
    lands: ["Soul", "Royale", "Unity", "Flatspin", "Mute"],
    ride: "skates",
  },
  {
    id: "park-bmx-flair",
    name: "On the BMX: a Flair on vert, the bike's special",
    mapId: "park",
    // The bike's specials are body flips (#138). With the meter full it
    // crouches all the way to the quarter for speed, and right, down + grab
    // just after it leaves the lip: a backflip with a 180 that comes round in
    // time to ride back down.
    start: { x: 12.5, z: 10.5, yaw: N, speed: 0 },
    special: true,
    segments: [
      roll(0.2),
      ...stepOff(),
      roll(0.3),
      tap("spinL"),
      roll(0.3),
      ...stepOff(),
      crouch(3.1),
      ...flick([1, 0], [0, -1]),
      tap("grab"),
      roll(2),
    ],
    lands: ["Flair"],
    ride: "bmx",
  },
  {
    id: "park-bmx-nothing",
    name: "On the BMX: a Nothing on vert, R1 held as it grabs",
    mapId: "park",
    // DMFB's modifier (#138): R1 held as you grab pulls the bigger grab, here
    // a No-Hander's: hands and feet off, a Nothing. R1 is only down for the
    // press, so it barely spins, and the grab's let go in time to land.
    start: { x: 12.5, z: 10.5, yaw: N, speed: 0 },
    segments: [
      roll(0.2),
      ...stepOff(),
      roll(0.3),
      tap("spinL"),
      roll(0.3),
      ...stepOff(),
      crouch(3.4),
      roll(0.1),
      { dur: DEMO_STEP, x: 0, y: 0, hold: ["grab", "spinR"] },
      hold("grab", 0.5),
      roll(2),
    ],
    lands: ["Nothing"],
    ride: "bmx",
  },
  {
    id: "park-bmx-funbox-backflip",
    name: "On the BMX: a Backflip off the funbox kicker",
    mapId: "park",
    // A body flip comes round faster to fit a smaller air (#144): crouched up
    // the funbox kicker and popped off its face, up, down and flip a beat
    // after takeoff still lands it, quicker than on vert.
    start: { x: 0, z: 20, yaw: N, speed: 0 },
    segments: [
      roll(0.2),
      ...stepOff(),
      roll(0.3),
      tap("spinL"),
      roll(0.3),
      ...stepOff(),
      crouch(1.5),
      roll(0.2),
      ...flick([0, 1], [0, -1]),
      tap("flip"),
      roll(2.5),
    ],
    lands: ["Backflip"],
    ride: "bmx",
  },
  {
    id: "park-bmx-backflip",
    name: "On the BMX: a Backflip Tailwhip on vert, no special needed",
    mapId: "park",
    // Body flips are everyday tricks on the bike (#138): with no meter it
    // crouches up to the lip (letting go any lower hops it onto the ramp),
    // then up, down and flip just after takeoff backflips it, and flip again
    // mid-flip whips the bike round too.
    start: { x: 12.5, z: 10.5, yaw: N, speed: 0 },
    segments: [
      roll(0.2),
      ...stepOff(),
      roll(0.3),
      tap("spinL"),
      roll(0.3),
      ...stepOff(),
      crouch(3.4),
      roll(0.05),
      ...flick([0, 1], [0, -1]),
      tap("flip"),
      roll(0.2),
      tap("flip"),
      roll(2),
    ],
    lands: ["Backflip Tailwhip"],
    ride: "bmx",
  },
  {
    id: "park-bmx-superman-backflip",
    name: "On the BMX: a Superman Backflip on vert",
    mapId: "park",
    // Grab during a body flip (#138): the backflip line, but grab + down just
    // after it starts stretches out into a Superman on the way round, letting
    // go in time to catch the bars and ride back down.
    start: { x: 12.5, z: 10.5, yaw: N, speed: 0 },
    segments: [
      roll(0.2),
      ...stepOff(),
      roll(0.3),
      tap("spinL"),
      roll(0.3),
      ...stepOff(),
      crouch(3.4),
      roll(0.05),
      ...flick([0, 1], [0, -1]),
      tap("flip"),
      roll(0.1),
      tap("grab", 0, -1),
      roll(2),
    ],
    lands: ["Superman Backflip"],
    ride: "bmx",
  },
  {
    id: "park-bmx-double-whip",
    name: "On the BMX: a Backflip Double Tailwhip on vert",
    mapId: "park",
    // Two quick flip taps during a body flip (#138) whip the bike round twice.
    start: { x: 12.5, z: 10.5, yaw: N, speed: 0 },
    segments: [
      roll(0.2),
      ...stepOff(),
      roll(0.3),
      tap("spinL"),
      roll(0.3),
      ...stepOff(),
      crouch(3.4),
      roll(0.05),
      ...flick([0, 1], [0, -1]),
      tap("flip"),
      roll(0.1),
      tap("flip"),
      roll(0.05),
      tap("flip"),
      roll(2),
    ],
    lands: ["Backflip Double Tailwhip"],
    ride: "bmx",
  },
];
